import { useCallback, useEffect, useRef, useState } from "react";
import { Map as MLMap, Marker, setWorkerUrl } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { VECTOR_STYLE, ESRI_TILES, ESRI_ATTRIBUTION, hasWebGL2, isBrave, transformRequest } from "../lib/maps";
import { attachMapHealth, attachRasterHealth, greyBackground, vectorDisabled, rememberFallback, publishDiag } from "../lib/mapHealth";
import { MapPlaceholder } from "./MapPlaceholder";
import { MapChrome } from "./MapChrome";

setWorkerUrl(`${process.env.PUBLIC_URL}/maplibre-gl-worker.mjs`);
const pinIcon = L.divIcon({ className: "", html: '<div class="pin-marker"></div>', iconSize: [28, 28], iconAnchor: [14, 28] });

const VectorStatic = ({ lat, lng, zoom, onFail, onReady, onContextLost }) => {
  const el = useRef(null);
  useEffect(() => {
    const m = new MLMap({ container: el.current, style: VECTOR_STYLE, center: [lng, lat], zoom, interactive: false, attributionControl: { compact: true }, transformRequest });
    const dispose = attachMapHealth(m, { onFail, onReady: () => { greyBackground(m); onReady(); } });
    m.once("style.load", () => greyBackground(m));
    let disposed = false;
    m.getCanvas().addEventListener("webglcontextlost", () => { if (!disposed) onContextLost(); }, { once: true });  // remove() also loses the context
    const node = document.createElement("div"); node.className = "pin-marker";
    new Marker({ element: node, anchor: "bottom" }).setLngLat([lng, lat]).addTo(m);
    const ro = new ResizeObserver(() => m.resize()); ro.observe(el.current);
    return () => { disposed = true; dispose(); ro.disconnect(); m.remove(); };
  }, [lat, lng, zoom, onFail, onReady, onContextLost]);
  return <div ref={el} className="static-map" data-testid="static-map" data-provider="vector" />;
};

const RasterStatic = ({ lat, lng, zoom, onFail, onReady }) => {
  const el = useRef(null);
  useEffect(() => {
    const m = L.map(el.current, { zoomControl: false, dragging: false, scrollWheelZoom: false, doubleClickZoom: false, touchZoom: false, boxZoom: false, keyboard: false, attributionControl: true }).setView([lat, lng], zoom);
    const layer = L.tileLayer(ESRI_TILES, { maxZoom: 19, crossOrigin: "anonymous", attribution: ESRI_ATTRIBUTION }).addTo(m);
    const dispose = attachRasterHealth(layer, { onFail, onReady });
    L.marker([lat, lng], { icon: pinIcon, interactive: false }).addTo(m);
    el.current.querySelectorAll(".leaflet-control-attribution").forEach((n) => { n.classList.add("notranslate"); n.setAttribute("translate", "no"); });
    const ro = new ResizeObserver(() => m.invalidateSize()); ro.observe(el.current);
    return () => { dispose(); ro.disconnect(); m.remove(); };
  }, [lat, lng, zoom, onFail, onReady]);
  return <div ref={el} className="static-map" data-testid="static-map" data-provider="esri" />;
};

const initialProvider = () => (hasWebGL2() && !vectorDisabled() ? "vector" : ESRI_TILES ? "esri" : "none");

/* Non-interactive map (contractor page, job detail): same vector → Esri → placeholder self-healing as the picker. */
export const StaticMap = ({ lat, lng, zoom = 16, nav = true, t }) => {
  const [provider, setProvider] = useState(initialProvider);
  const [gen, setGen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [switched, setSwitched] = useState(() => vectorDisabled());
  const fail = useCallback((diag) => {
    setProvider((p) => { if (p === "vector" && diag?.provider !== "esri") { rememberFallback(diag); setSwitched(true); return ESRI_TILES ? "esri" : "none"; } return "none"; });
    setLoading(false); setGen((g) => g + 1);
  }, []);
  const ready = useCallback(() => setLoading(false), []);
  const reinit = useCallback(() => { setProvider(initialProvider()); setSwitched(vectorDisabled()); setLoading(true); setGen((g) => g + 1); }, []);
  const losses = useRef([]);
  const contextLost = useCallback(() => {
    const now = Date.now(); losses.current = [...losses.current.filter((t) => now - t < 60000), now];
    if (losses.current.length > 2) return fail(publishDiag({ provider: "openfreemap", reason: "webglcontextlost-repeated", failedResources: losses.current.length }));
    if (!document.hidden) return reinit();
    const onVis = () => { if (!document.hidden) { document.removeEventListener("visibilitychange", onVis); reinit(); } };
    document.addEventListener("visibilitychange", onVis);
  }, [reinit, fail]);
  return (
    <div className="static-map-wrap" data-testid="static-map-wrap" data-provider={provider === "vector" ? "openfreemap" : provider} data-loading={loading ? "true" : "false"}>
      {provider === "vector" && <VectorStatic key={`v${gen}`} lat={lat} lng={lng} zoom={zoom} onFail={fail} onReady={ready} onContextLost={contextLost} />}
      {provider === "esri" && <RasterStatic key={`r${gen}`} lat={lat} lng={lng} zoom={zoom} onFail={fail} onReady={ready} />}
      {provider === "none" && <MapPlaceholder lat={lat} lng={lng} nav={nav} className="static-map" />}
      <MapChrome loading={loading && provider !== "none"} switched={switched} onReload={reinit} t={t} compact />
      {switched && isBrave() && <p className="map-brave-hint" data-testid="brave-hint">{t?.braveHint || "Brave's Shields can block map rendering — tap the lion icon and set Shields to Standard or allow fingerprinting for dropit.fyi."}</p>}
    </div>
  );
};
